/**
 * Route-level error boundary — the router's `errorElement`.
 *
 * Mounted once on the root route, so it catches anything thrown while rendering
 * a page element or resolving a route's lazy import. Without it React Router
 * falls back to its own developer-facing screen ("Unexpected Application
 * Error!"), which is what production users were being shown.
 *
 * Its first job is the stale-chunk case: after a deploy, an open tab asks for
 * route chunks the server no longer has. That is recoverable — reload onto the
 * new build — so it recovers silently instead of rendering an error at all.
 */

import { NxSpinner } from "@agent-nexus/ui"
import { useEffect, useState } from "react"
import { useRouteError } from "react-router"

import { handleError } from "@/common/utils/error-handler"
import {
  attemptStaleChunkReload,
  isStaleChunkError,
  isStaleChunkReloadInFlight
} from "@/common/utils/stale-chunk.util"

import { ErrorSurface, FeaturedIcon, FeaturedMedia } from "./ErrorSurface"
import { ReloadButton } from "./ReloadButton"

/**
 * Shown while the automatic reload is in flight. Deliberately not an error —
 * the app is a moment away from replacing itself, and flashing a failure state
 * would be a lie the user reads before the page disappears.
 */
function ReloadingState() {
  return (
    <ErrorSurface
      live
      media={
        <FeaturedMedia decorative>
          <NxSpinner size="lg" className="text-nx-text-secondary" />
        </FeaturedMedia>
      }
      title="Updating to the latest version"
      description="A new version was just released. One moment while we bring you up to date."
    />
  )
}

/**
 * Shown when the automatic reload already ran and we still landed here — i.e.
 * reloading did not fix it. Reloading again on our own would be a refresh loop,
 * so the next attempt has to be the user's decision.
 */
function UpdateRequiredState() {
  return (
    <ErrorSurface
      media={<FeaturedIcon name="arrow-rotate-counter-clockwise" />}
      title="A new version is available"
      description="This page couldn't finish loading. Reloading will pick up the latest version."
      actions={<ReloadButton>Reload</ReloadButton>}
    />
  )
}

/**
 * The non-recoverable case: a genuine render error, not a stale chunk. Reloading
 * often clears a transient failure, and the boundary has already reported the
 * error to Sentry — so the copy can honestly say the team was notified.
 */
function UnexpectedErrorState() {
  return (
    <ErrorSurface
      media={<FeaturedIcon name="triangle-exclamation" tone="error" />}
      title="Something went wrong"
      description="An unexpected error interrupted this page. Reloading usually fixes it — if it keeps happening, our team has already been notified."
      actions={<ReloadButton>Reload page</ReloadButton>}
    />
  )
}

export function RouteErrorBoundary() {
  const error = useRouteError()
  const isStale = isStaleChunkError(error)

  // Starts optimistic for a stale chunk: the effect below either reloads (and
  // this state is never seen for long) or flips to the manual card.
  const [reloading, setReloading] = useState(isStale)

  useEffect(() => {
    handleError(error, {
      component: "RouteErrorBoundary",
      operation: isStale ? "stale_chunk" : "route_render",
      silent: true,
      extra: { staleChunk: isStale }
    })

    if (!isStale) return

    // The preloadError listener usually gets here first and has a reload on the
    // way; this render is just that same failure arriving through React. Wait it
    // out rather than reloading twice or reading it as a failed recovery.
    if (isStaleChunkReloadInFlight()) return

    // A suppressed reload means recovery already failed once this session.
    if (!attemptStaleChunkReload()) setReloading(false)
  }, [error, isStale])

  if (isStale) return reloading ? <ReloadingState /> : <UpdateRequiredState />

  return <UnexpectedErrorState />
}
